/**
 * Cài đặt của app — Prompt.md §23, §25, §27 (màn hình Settings ở Bước 15).
 *
 * Sống ở `apps/web/src/app` chứ không ở `packages/shared-config`: đây là **lựa chọn của người
 * dùng trên một thiết bị**, không phải hằng số của hệ thống. `shared-config` giữ default và
 * `AppConfigOverrides`; file này giữ những gì người dùng bấm chọn và được ghi xuống store
 * `settings` của IndexedDB.
 */

import { ISSUE_AVAILABILITY } from "@studystraight/posture-core";
import type { AppConfigOverrides } from "@studystraight/shared-config";
import type {
  CameraPlacement,
  PostureIssueType,
  SessionOptions,
  TimerPreset,
} from "@studystraight/shared-types";

import type { CameraFacing } from "../platform/camera/CameraService.js";

export interface AppSettings {
  /**
   * Chế độ đặt máy mặc định (BLOCKER #1 Option C). Đổi ở Settings **không** ảnh hưởng phiên đang
   * chạy: baseline đã hiệu chỉnh theo một placement cụ thể, nên đổi = phải calibrate lại ở phiên
   * sau (xem `SessionSetup` trong navigation.ts).
   */
  placement: CameraPlacement;
  /**
   * Camera trước/sau đã chọn.
   *
   * Phải nằm trong settings chứ không phải state của từng screen: mỗi screen tự tạo `useCamera()`
   * riêng, nên trước đây bấm "Đổi camera" ở Camera Setup xong sang Calibration là quay về camera
   * trước — hiệu chỉnh baseline bằng một camera khác camera lúc canh khung hình.
   */
  facingMode: CameraFacing;
  timerPreset: TimerPreset;
  /** Chỉ dùng khi `timerPreset === "custom"`. */
  customDurationMinutes: number;
  /** §20 — tắt giọng nói nhưng vẫn giữ chime + băng chữ. */
  voiceEnabled: boolean;
  eyeBreakEnabled: boolean;
  /** §25 — cho phép mở panel debug ở Setup/Monitoring. */
  debugMode: boolean;
  /**
   * Panel debug đang mở hay đóng.
   *
   * Tách khỏi `debugMode` (quyền) vì đây là **trạng thái** — và nó phải sống lâu hơn một screen:
   * việc tune ngưỡng ở §34 Phase 12 là đi Setup → Calibration → Monitoring liên tục, mỗi lần
   * chuyển screen lại phải bấm "Debug" là mất luôn mấy giây có số để đọc.
   */
  debugPanelOpen: boolean;
  /**
   * Tuning ngưỡng ở Settings > Developer (§14, §34 Phase 12).
   *
   * Lưu dạng `AppConfigOverrides` để đưa thẳng vào `resolveConfig(placement, overrides)` — không
   * tự định nghĩa lại một shape thứ hai cho threshold, vì hai shape sẽ lệch nhau ngay lần đầu ai
   * đó thêm một ngưỡng mới.
   *
   * Tách **theo placement**: cùng một độ cúi đầu tạo ra delta góc khác nhau giữa hai chế độ đặt
   * máy (BLOCKER #1), nên một bộ override dùng chung sẽ phá kết quả tune của chế độ còn lại.
   */
  overrides: PlacementOverrides;
}

export type PlacementOverrides = Record<CameraPlacement, AppConfigOverrides>;

/** §23 */
export const TIMER_PRESETS: readonly TimerPreset[] = [25, 30, 45, 60, "custom"];

/** §28 — MVP một trẻ, một profile; id cố định để §28 sau này chỉ phải thêm UI chọn trẻ. */
export const LOCAL_CHILD_ID = "local-default";
export const LOCAL_PROFILE_ID = "local-default";

export const defaultAppSettings: AppSettings = {
  placement: "FRONTAL",
  facingMode: "user",
  timerPreset: 25,
  customDurationMinutes: 20,
  voiceEnabled: true,
  eyeBreakEnabled: true,
  debugMode: false,
  debugPanelOpen: false,
  overrides: { FRONTAL: {}, SIDE: {} },
};

export function overridesFor(settings: AppSettings, placement: CameraPlacement): AppConfigOverrides {
  return settings.overrides[placement];
}

/**
 * Issue này có đo được ở chế độ đặt máy này không.
 *
 * Đọc lại đúng bảng mà `evaluatePosture` dùng thay vì chép danh sách sang UI: nếu sau này mode SIDE
 * đo được lệch vai, Settings không được tiếp tục ẩn ô ngưỡng đó.
 */
export function isIssueMeasurable(placement: CameraPlacement, issue: PostureIssueType): boolean {
  return ISSUE_AVAILABILITY[placement][issue];
}

/**
 * Dựng `SessionOptions` từ settings.
 *
 * `placement` truyền rời thay vì lấy `settings.placement`: phiên đang chạy dùng placement đã đóng
 * băng lúc calibrate, và nếu người dùng vào Settings đổi placement giữa phiên thì Report vẫn phải
 * ghi đúng placement mà số liệu được đo bằng.
 */
export function toSessionOptions(settings: AppSettings, placement: CameraPlacement): SessionOptions {
  const custom = Math.round(settings.customDurationMinutes) * 60_000;
  return {
    childId: LOCAL_CHILD_ID,
    profileId: LOCAL_PROFILE_ID,
    placement,
    timerPreset: settings.timerPreset,
    ...(settings.timerPreset === "custom" && custom > 0 ? { customDurationMs: custom } : {}),
    eyeBreakEnabled: settings.eyeBreakEnabled,
  };
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readPreset(value: unknown): TimerPreset | null {
  if (value === "custom") return "custom";
  return TIMER_PRESETS.includes(value as TimerPreset) ? (value as TimerPreset) : null;
}

/**
 * Đọc settings đã lưu.
 *
 * Dữ liệu từ IndexedDB là `unknown` **thật sự**: nó có thể do một bản app cũ hơn ghi ra. Một
 * `as AppSettings` ở đây sẽ biến một field thiếu thành `undefined` chạy xuyên qua `resolveConfig`
 * rồi hỏng ở một chỗ hoàn toàn khác, nên mỗi field được kiểm riêng và về default nếu sai kiểu.
 */
function readOverrides(value: unknown): PlacementOverrides {
  if (!isPlainObject(value)) return { FRONTAL: {}, SIDE: {} };
  return {
    FRONTAL: isPlainObject(value.FRONTAL) ? (value.FRONTAL as AppConfigOverrides) : {},
    SIDE: isPlainObject(value.SIDE) ? (value.SIDE as AppConfigOverrides) : {},
  };
}

export function normalizeSettings(value: unknown): AppSettings {
  if (!isPlainObject(value)) return defaultAppSettings;
  const preset = readPreset(value.timerPreset);
  const minutes = value.customDurationMinutes;
  return {
    placement: value.placement === "SIDE" ? "SIDE" : "FRONTAL",
    facingMode: value.facingMode === "environment" ? "environment" : "user",
    timerPreset: preset ?? defaultAppSettings.timerPreset,
    customDurationMinutes:
      typeof minutes === "number" && Number.isFinite(minutes) && minutes > 0
        ? minutes
        : defaultAppSettings.customDurationMinutes,
    voiceEnabled: value.voiceEnabled !== false,
    eyeBreakEnabled: value.eyeBreakEnabled !== false,
    debugMode: value.debugMode === true,
    debugPanelOpen: value.debugPanelOpen === true,
    overrides: readOverrides(value.overrides),
  };
}
